// admin/src/features/review/candidates.ts
//
// Pure helpers for the Review page: parsing/choosing among AI-generated
// demo-photo candidates in the public `exercise-media` bucket, uploaded
// by a script at `candidates/<exercise_id>/<timestamp>.png` (one or more
// per exercise -- the script may leave a stale one behind if it reran
// before this page cleaned up). No Supabase import here so this stays
// trivially testable.

/** A single candidate object found under `candidates/<exerciseId>/`. */
export type CandidateFile = {
  exerciseId: string;
  /** File name only (e.g. "1730000000000.png"), not the full path. */
  name: string;
  /** Storage `created_at` (ISO) when available -- the most reliable "latest" signal. */
  createdAt: string | null;
};

/** `candidates/<exerciseId>/<name>` for a candidate file. */
export function candidatePath(exerciseId: string, name: string): string {
  return `candidates/${exerciseId}/${name}`;
}

/**
 * Supabase storage's `list()` on a prefix returns immediate children only:
 * subfolders show up as entries with `id === null` (and no file metadata).
 * Given a listing of `candidates/`, this returns just the exercise-id
 * subfolder names, skipping any stray file placed directly under
 * `candidates/` (shouldn't happen, but a loose object there is not a
 * candidate for any exercise).
 */
export function folderNamesFromListing(entries: Array<{ name: string; id: string | null }>): string[] {
  return entries.filter((e) => e.id === null && e.name).map((e) => e.name);
}

/**
 * Compares two candidate timestamps so the greater (later) one sorts
 * last. Prefers `createdAt` (ISO strings compare correctly as plain
 * strings); when either is missing, falls back to the file name, which
 * is itself expected to be a sortable timestamp (e.g. epoch millis or an
 * ISO-ish string) -- numeric names are compared numerically so
 * "2000000000000.png" > "999999999999.png" even though it's shorter.
 */
export function compareCandidateRecency(a: CandidateFile, b: CandidateFile): number {
  if (a.createdAt && b.createdAt) {
    return a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0;
  }
  if (a.createdAt && !b.createdAt) return 1;
  if (!a.createdAt && b.createdAt) return -1;

  const aNum = Number.parseInt(a.name, 10);
  const bNum = Number.parseInt(b.name, 10);
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) return aNum - bNum;
  return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
}

/** One candidate card's worth of data: the exercise it's for, and the single file to show/act on. */
export type LatestCandidate = { exerciseId: string; file: CandidateFile };

/**
 * Groups candidate files by exercise and keeps only the most recent one
 * per exercise (a regen that ran before the previous candidate was
 * cleaned up should not produce two cards for the same exercise).
 */
export function pickLatestPerExercise(files: CandidateFile[]): LatestCandidate[] {
  const latestByExercise = new Map<string, CandidateFile>();
  for (const file of files) {
    const current = latestByExercise.get(file.exerciseId);
    if (!current || compareCandidateRecency(file, current) > 0) {
      latestByExercise.set(file.exerciseId, file);
    }
  }
  return Array.from(latestByExercise.entries()).map(([exerciseId, file]) => ({ exerciseId, file }));
}

/** Any stale candidates for an exercise beyond the one `pickLatestPerExercise` kept -- worth deleting so the bucket doesn't accumulate orphans. */
export function staleCandidatesFor(exerciseId: string, files: CandidateFile[], kept: CandidateFile): CandidateFile[] {
  return files.filter((f) => f.exerciseId === exerciseId && f.name !== kept.name);
}

/** Splits `items` into chunks of at most `size`, for bounded-concurrency batches of storage calls. */
export function chunk<T>(items: T[], size: number): T[][] {
  if (size <= 0) throw new Error('chunk size must be positive');
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export type ReviewCard = {
  exerciseId: string;
  candidatePath: string;
  exercise: { name_en: string; name_he: string; sport: string; muscle_group: string } | null;
  existingDemoPath: string | null;
  /** The demo_photo row's id, needed to replace it in place on approve -- not relevant to sorting/display. */
  existingDemoId?: string | null;
};

/** Sorts review cards so exercises that already have a demo photo (a straight swap, lower priority) come after ones with none. */
export function sortReviewCards(cards: ReviewCard[]): ReviewCard[] {
  return [...cards].sort((a, b) => {
    const aHas = a.existingDemoPath ? 1 : 0;
    const bHas = b.existingDemoPath ? 1 : 0;
    if (aHas !== bHas) return aHas - bHas;
    const an = a.exercise?.name_en ?? a.exerciseId;
    const bn = b.exercise?.name_en ?? b.exerciseId;
    return an.localeCompare(bn);
  });
}
